import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import config from "config";
import {
  Box,
  Button,
  Flex,
  FormControl,
  Heading,
  Input,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
import DefaultAuth from "layouts/auth/Default";
import illustration from "assets/img/auth/auth.png";
import toast from "react-hot-toast";

function Verify() {
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email;

  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = "gray.400";
  const textColorBrand = useColorModeValue("brand.500", "white");

  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);

  const inputRefs = useRef([]);

  const sendOtp = useCallback(async () => {
  const otpPromise = fetch(`${config.BACKEND_API_URL}/auth/send-otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ email })
  }).then(async (res) => {
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  });

  toast.promise(otpPromise, {
    loading: "Sending OTP...",
    success: (data) => data.message || "OTP sent",
    error: (err) => err.message || "Failed to send OTP",
  });

  try {
    await otpPromise;
    setTimer(60);
    setCanResend(false);
  } catch {}
}, [email]);
  
  useEffect(() => {
    if (!email) {
      navigate("/auth/sign-up");
      return;
    }
    sendOtp();
  }, [email, navigate, sendOtp]);

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (isNaN(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const data = e.clipboardData.getData("text").trim();
    if (data.length === 6 && /^\d+$/.test(data)) {
      setOtp(data.split(""));
      inputRefs.current[5].focus();
    }
    e.preventDefault();
  };

  const handleVerify = async () => {
  const code = otp.join("");

  if (code.length !== 6) {
    return toast.error("Enter 6-digit code");
  }

  const verifyPromise = fetch(`${config.BACKEND_API_URL}/auth/verify-account`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({ email, otp: code })
  }).then(async (res) => {
    const data = await res.json();

    if (!data.success) {
      throw new Error(data.message);
    }

    return data;
  });

  toast.promise(verifyPromise, {
    loading: "Verifying...",
    success: (data) => data.message || "Verified",
    error: (err) => err.message || "Verification failed",
  });

  try {
    setLoading(true);
    await verifyPromise;
    navigate("/auth/sign-in");
  } catch {}
  finally {
    setLoading(false);
  }
};

  const handleResend = async () => {
    if (!canResend) return;
    await sendOtp();
  };

  return (
    <DefaultAuth illustrationBackground={illustration} image={illustration}>
      <Flex
        maxW={{ base: "100%", md: "max-content" }}
        w="100%"
        mx={{ base: "auto", lg: "0px" }}
        me="auto"
        h="100%"
        alignItems="start"
        justifyContent="center"
        mb={{ base: "30px", md: "60px" }}
        px={{ base: "25px", md: "0px" }}
        mt={{ base: "40px", md: "14vh" }}
        flexDirection="column"
      >
        <Box me="auto">
          <Heading color={textColor}>Verify Your Email</Heading>
          <Text color={textColorSecondary}>
            Enter the 6-digit code sent to your email
          </Text>
        </Box>

        <Flex
          zIndex="2"
          direction="column"
          w={{ base: "100%", md: "420px" }}
          maxW="100%"
          background="transparent"
          borderRadius="15px"
          mx={{ base: "auto", lg: "unset" }}
          me="auto"
          mb={{ base: "20px", md: "auto" }}
        >
          <FormControl>
            <Flex justifyContent="space-between" mb="24px" onPaste={handlePaste}>
              {otp.map((data, index) => (
                <Input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  key={index}
                  ref={(el) => (inputRefs.current[index] = el)}
                  variant="auth"
                  fontSize="2xl"
                  fontWeight="700"
                  textAlign="center"
                  size="lg"
                  w="50px"
                  h="60px"
                  p="0"
                  maxLength="1"
                  value={data}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                />
              ))}
            </Flex>

            <Button
              fontSize="sm"
              variant="brand"
              fontWeight="500"
              w="100%"
              h="50px"
              mb="24px"
              onClick={handleVerify}
              isLoading={loading}
              loadingText="Verifying..."
            >
              Verify
            </Button>
          </FormControl>

          <Flex flexDirection="column" mt="0px">
            <Text color={textColorSecondary} fontSize="14px">
              Didn't receive the code?{" "}
              {canResend ? (
                <Text
                  as="span"
                  color={textColorBrand}
                  fontWeight="500"
                  cursor="pointer"
                  onClick={handleResend}
                >
                  Resend Code
                </Text>
              ) : (
                <Text as="span" fontWeight="500">
                  Resend in {timer}s
                </Text>
              )}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </DefaultAuth>
  );
}

export default Verify;
